import { dimensions } from 'theme/constants';

/**
 * Generates initials from a name string
 * Takes the first character of the first two words and converts to lowercase
 *
 * @param name - The full name to extract initials from
 * @returns Two lowercase initials (e.g., "John Doe" -> "jd")
 *
 * @example
 * initials("John Doe") // "jd"
 * initials("Jane Smith") // "js"
 */
export const initials = (name: string) =>
  name
    ?.split(' ')
    .map(char => char?.[0]?.toLowerCase())
    .splice(0, 2)
    .join('');

/**
 * Calculates the scrollable height for a message drawer based on viewport and component dimensions
 * Subtracts the drawer offset, header, and shift card heights from the viewport
 *
 * @param headerHeight - Height of the header element in pixels
 * @param shiftCardHeight - Height of the shift card element in pixels
 * @param setScrollHeight - Callback function to update the scroll height value
 *
 * @example
 * calculateScrollHeight(60, 100, setScrollHeight)
 * // Updates scroll height to: calc(100vh - 16px - 60px - 100px)
 */
export const calculateScrollHeight = (
  headerHeight: number,
  shiftCardHeight: number,
  setScrollHeight: (height: string) => void
) => {
  const totalOffset = parseInt(dimensions.messageDrawerOffset, 10);

  const calculatedHeight = `calc(100vh - ${totalOffset}px - 
  ${headerHeight}px - ${shiftCardHeight}px)`;
  setScrollHeight(calculatedHeight);
};

/**
 * Wrapper for calculateScrollHeight that extracts dimensions from React refs
 * Safely retrieves offsetHeight from DOM elements before calculating scroll height
 *
 * @param headerRef - Ref to the header HTML element
 * @param shiftCardRef - Ref to the shift card HTML element
 * @param setScrollHeight - Callback function to update the scroll height value
 *
 * @example
 * calculateScrollHeightWithRefs(headerRef, shiftCardRef, setScrollHeight)
 */
export const calculateScrollHeightWithRefs = (
  headerRef: React.RefObject<HTMLElement>,
  shiftCardRef: React.RefObject<HTMLElement>,
  setScrollHeight: (height: string) => void
) => {
  if (headerRef.current && shiftCardRef.current) {
    const headerHeight = headerRef.current.offsetHeight;
    const shiftCardHeight = shiftCardRef.current.offsetHeight;

    calculateScrollHeight(headerHeight, shiftCardHeight, setScrollHeight);
  }
};

/**
 * Handles API responses that may return 200 with empty body
 * When JSON parsing fails on empty responses, treats it as success
 *
 * @param promise - The API call promise to handle
 * @returns The resolved value or undefined if the response was empty
 *
 * @example
 * await handleEmptyJsonResponse(approveTradeRequest(id));
 */
export const handleEmptyJsonResponse = async <T>(
  promise: Promise<T>
): Promise<T | undefined> => {
  try {
    return await promise;
  } catch (error) {
    if (
      error instanceof SyntaxError &&
      error.message.includes('Unexpected end of JSON input')
    ) {
      // Empty response body is treated as success
      return undefined;
    }
    throw error;
  }
};
